// Package Imports
import React, { useRef, useState } from 'react';
import { OrderPayload as OrderPayloadType } from '@/lib/types/orderPayload';

// Components Imports
import StylizedButton from '@/components/stylizedButton';
import Toast from '@/components/toast';

// Style Imports 
import styles from './cartCheckoutPromoCodes.module.scss';
import { v4 as uuidv4 } from "uuid";

type UpdateOrderTotalsProps = {
  updatedOrder: OrderPayloadType;
  orderData: OrderPayloadType | null;
  setOrderData: React.Dispatch<React.SetStateAction<OrderPayloadType | null>>;
};

type CartCheckoutCouponsProps = {
  orderData: OrderPayloadType | null;
  setOrderData: React.Dispatch<React.SetStateAction<OrderPayloadType | null>>;
}

export default function CartCheckoutCoupons({ setOrderData, orderData }: CartCheckoutCouponsProps) {
  const token = orderData?.token;
  const [loading, setLoading] = useState(false);
  const couponCodeRef = useRef<HTMLInputElement>(null);
  const [couponCodeErrorMessage, setCouponCodeErrorMessage] = useState<string | null>(null);

  const handleClick = async () => {
    let couponCode = couponCodeRef.current?.value ?? '';
    couponCode = couponCode.trim().toUpperCase();
    if (!couponCode) {
      return;
    }
    setLoading(true);
    try {
      const uri = `${process.env.NEXT_PUBLIC_SITE_URL}/api/coupon/${token}`;
      const res = await fetch(uri, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ couponCode: couponCode }),
      });
      if (res.ok) {
        setCouponCodeErrorMessage(null);
        const updatedOrder = await res.json();
        updateOrderTotals({ updatedOrder, orderData, setOrderData });
        if (couponCodeRef.current) {
          couponCodeRef.current.value = '';
        }
      } else {
        if (400 === res.status) {
          setCouponCodeErrorMessage(`Sorry, the coupon code ${couponCode} you entered is invalid or expired.`);
        } else {
          setCouponCodeErrorMessage('Sorry, there was an error applying the coupon.');
        }

      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.toasts} role="alert" aria-atomic="true">
        {couponCodeErrorMessage && <Toast id={uuidv4()} type="error" label={true} message={couponCodeErrorMessage} />}
      </div>
      <section className={styles.cartCheckoutPromoCodes}>
        <label htmlFor="promo-code">Promo Code</label>
        <div>
          <input id="promo-code" type="text" ref={couponCodeRef} />
          <StylizedButton
            onClick={handleClick}
            disabled={loading}
            variant="secondary"
            size="mini"
          >
            {loading ? 'Applying...' : 'Apply Coupon'}
          </StylizedButton>
        </div>
      </section>
    </>
  );
}

const updateOrderTotals = ({ updatedOrder, orderData, setOrderData }: UpdateOrderTotalsProps) => {
  if (!orderData) return;

  const updatedOrderState = {
    ...orderData,
    pricing: updatedOrder.pricing,
  };
  setOrderData(updatedOrderState);
};
